"use strict";
/**
 * Central registry of all non-CRUD "custom actions" that can be gated by Role.
 *
 * Each action has a stable key (e.g. `service-history.approve`) that:
 *   - Is stored in `Role.permissions[].actions[]` to grant the action.
 *   - Is checked by the backend `requireAction(key)` middleware.
 *   - Is referenced by the client `rowActions`/`bulkActions` configs so the UI
 *     only renders buttons the user is allowed to run.
 *
 * Adding a new custom action requires ONLY adding an entry here and wiring the
 * endpoint on the server with `requireAction('<key>')` — no client changes.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ACTION_REGISTRY = void 0;
exports.actionsForSlug = actionsForSlug;
exports.actionByKey = actionByKey;
exports.ACTION_REGISTRY = [
    // Service History actions target the REPORT slug (`services_page`), not the
    // `service_history` form. Every other action in this registry already does
    // the same, and the Roles UI only renders its "Custom actions" column for
    // report-type resources (roles/detail.tsx) — so an action pointed at a form
    // was invisible to administrators. That is why these five could not be
    // granted or revoked through the app at all and had to be set by script.
    //
    // `requireAction` matches on resourceSlug, so the already-granted keys had
    // to move from each role's service_history entry to its services_page entry
    // in the same change — see seed/move-action-keys.ts.
    // ---- Service History field gates ----
    {
        key: 'service-history.change-service-type',
        label: 'Change service type',
        resourceSlug: 'services_page',
        scope: 'field',
        description: 'Allow this role to edit the Service Type field on a draft Service History record.',
    },
    // ---- Service History approval ----
    {
        key: 'service-history.approve',
        label: 'Approve',
        resourceSlug: 'services_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/servicing/history/:id/approve',
        description: 'Mark this service record as approved.',
    },
    {
        key: 'service-history.reject',
        label: 'Reject',
        resourceSlug: 'services_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/servicing/history/:id/reject',
        promptReason: true,
        variant: 'destructive',
        description: 'Reject this service record with a reason.',
    },
    {
        key: 'service-history.bulk-approve',
        label: 'Approve selected',
        resourceSlug: 'services_page',
        scope: 'bulk',
        method: 'POST',
        endpoint: '/servicing/history/bulk-approve',
        confirm: 'Approve all selected service records?',
    },
    {
        key: 'service-history.bulk-reject',
        label: 'Reject selected',
        resourceSlug: 'services_page',
        scope: 'bulk',
        method: 'POST',
        endpoint: '/servicing/history/bulk-reject',
        promptReason: true,
        variant: 'destructive',
    },
    // ---- Employee portal access (gates both single + bulk endpoints) ----
    // Declared on `employees_page` (the report) so the Role editor's
    // "Custom actions" dropdown surfaces them under the same row where
    // admins manage view / add / edit / delete for the Employees
    // report. The Role editor only renders the dropdown for report-
    // type resources (`roles/detail.tsx:391`); form-row actions stay
    // invisible. The `requireAction` middleware looks the key up
    // across every entry on the role, so the slug only drives UI
    // grouping — server-side enforcement is unchanged.
    {
        key: 'employee.invite-portal',
        label: 'Allow Portal Login',
        resourceSlug: 'employees_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/employees/:id/invite-portal',
        description: 'Enable portal login for an Employee (single or bulk). Requires the Employee to have an email set.',
    },
    {
        key: 'employee.assign-department',
        label: 'Assign Department',
        resourceSlug: 'employees_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/employees/:id/assign-department',
        description: 'Set or change the department on an Employee (single or bulk).',
    },
    {
        key: 'employee.unassign-department',
        label: 'Unassign Department',
        resourceSlug: 'employees_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/employees/:id/unassign-department',
        description: 'Clear the department on an Employee (single or bulk).',
        variant: 'destructive',
    },
    {
        key: 'employee.deactivate',
        label: 'Deactivate',
        resourceSlug: 'employees_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/employees/:id/deactivate',
        description: 'Archive an Employee and revoke portal login (single or bulk). Only applicable to records that currently have portal login enabled.',
        variant: 'destructive',
    },
    {
        key: 'employee.reactivate',
        label: 'Reactivate',
        resourceSlug: 'employees_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/employees/:id/reactivate',
        description: 'Flip an Archived Employee back to Active (single or bulk). Portal login is left off — admins separately invite via Allow Portal Login if needed.',
    },
    // ---- Job Card workflow ----
    // Declared on `job_cards_page` (the report) rather than `job_cards`
    // (the form) so the Role editor groups them under the same row
    // where admins tick the View permission — that's the operator's
    // mental model ("can this role do things on the Job Cards report?").
    // The `requireAction` middleware looks the key up across every
    // permission entry on the role, so the slug only drives UI grouping.
    {
        key: 'job-card.assign',
        label: 'Assign technician',
        resourceSlug: 'job_cards_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/fleet/job-cards/:id/assign',
        description: 'Assign this job card to a technician.',
    },
    // Start / Pause / Submit are operationally a single flow performed by
    // the same actor (the mechanic running the job), so they share one
    // grant: `job-card.service`. The three server endpoints
    // (/start, /pause, /submit) still exist as separate routes — they
    // just gate on the same action key now. Roles get a single
    // "Start Service" checkbox in the Role editor instead of three.
    {
        key: 'job-card.service',
        label: 'Start Service',
        resourceSlug: 'job_cards_page',
        scope: 'single',
        method: 'POST',
        // Endpoint is a representative one for help-text; the action gates
        // all three of /start, /pause, /submit.
        endpoint: '/fleet/job-cards/:id/start',
        description: 'Service a job card end-to-end: start, pause / resume the timer, and submit for review.',
    },
    // Post-completion edit of a Job Card's linked Service Details. Two
    // tiers: the plain `edit-service` action keeps the meter validations
    // (odometer / engine hours / hubo must be >= the previous reading on
    // the asset); `edit-service-override` lets the role bypass those
    // checks for back-fill / correction scenarios. UI shows an "Edit"
    // button on the View Service page when either grant is present;
    // server-side guard accepts the override only when the role carries
    // the override key.
    {
        key: 'job-card.edit-service',
        label: 'Edit Service',
        resourceSlug: 'job_cards_page',
        scope: 'field',
        description: 'Edit a completed job card’s service details. Standard meter validations apply (readings must be ≥ the previous reading on the asset).',
    },
    {
        key: 'job-card.edit-service-override',
        label: 'Edit Service and Override',
        resourceSlug: 'job_cards_page',
        scope: 'field',
        description: 'Edit a completed job card’s service details AND bypass meter validations (odometer / engine hours / hubo) for correcting prior data.',
    },
    {
        key: 'job-card.approve',
        label: 'Approve',
        resourceSlug: 'job_cards_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/fleet/job-cards/:id/approve',
        description: 'Approve the submitted job card.',
    },
    {
        key: 'job-card.reject',
        label: 'Reject',
        resourceSlug: 'job_cards_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/fleet/job-cards/:id/reject',
        description: 'Reject the submitted job card (terminal).',
        variant: 'destructive',
    },
    // Reverse transitions — operationally an admin escape hatch when a card
    // was approved/voided/completed prematurely. They clear the audit fields
    // tied to the transition being undone (reviewedAt/By on
    // revert-to-completed; submissions + submittedAt/By + completedTime on
    // revert-to-active) so the next forward pass writes a fresh audit trail.
    {
        key: 'job-card.revert-to-completed',
        label: 'Revert to Completed',
        resourceSlug: 'job_cards_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/fleet/job-cards/:id/revert-to-completed',
        confirm: 'Revert this job card to Completed status? Review will be cleared.',
        description: 'Move an approved or voided card back to Completed so it can be re-reviewed. Clears the prior review decision.',
        variant: 'destructive',
    },
    {
        key: 'job-card.revert-to-active',
        label: 'Revert to Active',
        resourceSlug: 'job_cards_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/fleet/job-cards/:id/revert-to-active',
        confirm: 'Revert this job card to Active? All submissions will be cleared.',
        description: 'Move a completed card back to Active so technicians can re-submit. Clears submission and completion timestamps.',
        variant: 'destructive',
    },
    // ---- Asset Setting (Configuration) ----
    {
        key: 'asset-setting.activate',
        label: 'Activate Configuration',
        resourceSlug: 'asset_onboarding_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/asset-settings/:id/activate',
        confirm: 'Activate this configuration? The currently active one will become inactive.',
        description: 'Mark this Configuration as active for its kind. Exactly one Configuration per kind is active at a time.',
    },
    // ---- Asset transfer lifecycle ----
    {
        // Both this and `onboard-asset.edit-record` replace an `editAdminOnly`
        // flag that keyed off systemProfile. The intent was to funnel people
        // through the guided wizard rather than the raw report form; that intent
        // survives, but who is exempt is now a role decision like everything else.
        key: 'asset-transfer.edit-record',
        label: 'Edit transfer records directly',
        resourceSlug: 'asset_transfer_page',
        scope: 'field',
        description: 'Add and edit transfer records straight from the report, bypassing the guided Transfer wizard. Without this the report is read-only and the wizard is the only route.',
    },
    {
        key: 'asset-transfer.finalize',
        label: 'Finalize transfer',
        resourceSlug: 'asset_transfer_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/asset-transfers/:id/finalize',
        confirm: 'Apply this transfer to the asset record? Department/Company/etc will be updated.',
        description: 'Apply the transfer answers onto the linked Asset and lock the transfer.',
    },
    {
        // Retry a failed IoT supplier email from the Email History report.
        // Regenerates the message from the linked Asset and the supplier's
        // current template rather than replaying the stored row — EmailHistory
        // only keeps a 240-char plaintext preview, not the sent HTML.
        key: 'email-history.retry',
        label: 'Retry send',
        resourceSlug: 'settings_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/email-history/:id/retry',
        confirm: 'Regenerate and resend this supplier email? It uses the template and recipients configured now, which may differ from the original attempt.',
        description: 'Re-send a failed IoT supplier email, rebuilt from the linked asset and the current template.',
    },
    // ---- Asset onboarding lifecycle ----
    {
        key: 'onboard-asset.edit-record',
        label: 'Edit onboarding records directly',
        resourceSlug: 'asset_onboarding_page',
        scope: 'field',
        description: 'Add and edit onboarding records straight from the report, bypassing the guided Onboard wizard. Without this the report is read-only and the wizard is the only route.',
    },
    {
        key: 'onboard-asset.finalize',
        label: 'Finalize onboarding',
        resourceSlug: 'asset_onboarding_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/onboard-assets/:id/finalize',
        confirm: 'Create the Asset record from this onboarding?',
        description: 'Create the live Asset record from this onboarding request.',
    },
    {
        key: 'onboard-asset.set-checklist',
        label: 'Mark checklist step',
        resourceSlug: 'asset_onboarding_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/onboard-assets/:id/set-checklist',
    },
    // ---- Asset bulk lifecycle actions (Assets report) ----
    // Tied to `assets_page` (the report) — the new role matrix only renders
    // custom-action toggles on report rows. Forms expose only Add.
    {
        key: 'asset.bulk-archive',
        label: 'Archive selected',
        resourceSlug: 'assets_page',
        scope: 'bulk',
        method: 'POST',
        endpoint: '/fleet/assets/bulk-archive',
        confirm: 'Archive all selected assets? They will be hidden from the active fleet.',
        description: 'Move the selected assets to the Archived list.',
    },
    {
        key: 'asset.bulk-dispose',
        label: 'Dispose selected',
        resourceSlug: 'assets_page',
        scope: 'bulk',
        method: 'POST',
        endpoint: '/fleet/assets/bulk-dispose',
        confirm: 'Dispose all selected assets? They will be removed from the active fleet.',
        variant: 'destructive',
        description: 'Move the selected assets to the Disposed list (terminal).',
    },
    {
        // Per-row offboarding form — opens a dialog with outcome + reason +
        // additional info + IoT-notify toggle. POSTs to /assets/:id/offboard
        // which updates vehicleStatus and explicitly sends the supplier's
        // offboard email so the result can be surfaced to the operator.
        key: 'asset.offboard',
        label: 'Offboard...',
        resourceSlug: 'assets_page',
        scope: 'single',
        method: 'POST',
        endpoint: '/fleet/assets/:id/offboard',
        description: 'Open the offboarding form to Archive or Dispose this asset and notify the IoT provider.',
    },
    {
        // Replaces a `systemProfile`-based lock on these three fields. That lock
        // keyed off a value nobody maintained — production had 186 employees on
        // 'Fleet User' and one on 'Admin' — so in practice it was absolute rather
        // than delegable. As an action it is grantable to whoever actually looks
        // after the IoT fleet.
        key: 'asset.edit-device-ids',
        label: 'Edit IoT device IDs',
        resourceSlug: 'assets_page',
        scope: 'field',
        description: 'Edit the per-provider IoT identifiers (GPS ID, EROAD ID, Blackhawk ID) on an asset. A wrong value cross-links the asset to another vehicle in the provider’s fleet, so this is kept separate from ordinary asset editing.',
    },
    {
        key: 'asset.bulk-nzta-update',
        label: 'NZTA Update',
        resourceSlug: 'assets_page',
        scope: 'bulk',
        method: 'POST',
        endpoint: '/fleet/assets/bulk-nzta-update',
        confirm: 'Fetch the latest NZTA data (WoF / CoF / rego expiry) for the selected assets?',
        description: 'Look up each selected asset via Carjam and refresh its WoF, CoF, and registration expiry dates.',
    },
    {
        key: 'asset.bulk-recompute-services',
        label: 'Recompute Service Report',
        resourceSlug: 'assets_page',
        scope: 'bulk',
        method: 'POST',
        endpoint: '/fleet/assets/bulk-recompute-services',
        description: 'Re-run the Service Report computation for the selected assets so the Service Report dashboard reflects the latest meter readings without waiting for the next cron.',
    },
    // ---- Asset tracking-provider sync (per-provider, fleet-wide) ----
    {
        key: 'asset.sync.eroad',
        label: 'Sync EROAD',
        resourceSlug: 'assets_page',
        scope: 'toolbar',
        method: 'POST',
        endpoint: '/sync-runs',
        description: 'Trigger an on-demand sync of every EROAD-tracked asset. Pulls fresh positions, updates matched assets, and bootstraps any new vehicles found in EROAD.',
    },
    {
        key: 'asset.sync.blackhawk',
        label: 'Sync Blackhawk',
        resourceSlug: 'assets_page',
        scope: 'toolbar',
        method: 'POST',
        endpoint: '/sync-runs',
        description: 'Trigger an on-demand sync of every Blackhawk-tracked asset. Pulls fresh positions, updates matched assets, and bootstraps any new vehicles found in Blackhawk.',
    },
    // ---- Admin / system ----
    {
        key: 'admin.recompute-upcoming-services',
        label: 'Recompute upcoming services',
        resourceSlug: 'settings_page',
        scope: 'bulk',
        method: 'POST',
        endpoint: '/admin/recompute-upcoming-services',
        description: 'Run the scheduler pass immediately.',
    },
    // ---- Dispatch Groups ----
    // Backs the "Assign Assets" dialog on the Dispatch Groups settings page —
    // takes a checked list of assets and snaps the group's membership to it
    // (reassigns from another group as needed, since `dispatchGroup` is a
    // single ref on Asset).
    {
        key: 'dispatch-group.sync-members',
        label: 'Assign Assets',
        resourceSlug: 'dispatch_groups_page',
        scope: 'bulk',
        method: 'POST',
        endpoint: '/master-data/dispatch-groups/:id/sync-members',
        description: 'Open a dialog to pick which assets belong to the selected dispatch group. Reassigns assets from their previous group and (optionally) flips Display in Scheduler on at the same time.',
    },
];
function actionsForSlug(slug) {
    return exports.ACTION_REGISTRY.filter((a) => a.resourceSlug === slug);
}
function actionByKey(key) {
    return exports.ACTION_REGISTRY.find((a) => a.key === key);
}
//# sourceMappingURL=actions.js.map